import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/endpoints'
import { useI18n } from '../i18n/I18nProvider'
import { Busy, ErrorBanner, Notice } from '../ui/controls'
import { useSession } from '../session/SessionProvider'
import type { MessageKey } from '../i18n/messages'
import type { CompletionCheck } from '../api/types'
import { emailPaths } from './paths'

/**
 * What is still missing before matching can start.
 *
 * It reads `/api/profile/me/completion`, which answers with field codes rather than sentences
 * (Group 17B) — so the wording lives here, in both languages, and the server never has to know
 * which language anyone reads. A code with no translation shows the code, which is ugly on
 * purpose: a silently prettified gap is a gap nobody fixes.
 */
export function Onboarding() {
  const { t } = useI18n()
  const { account } = useSession()

  const [profile, setProfile] = useState<CompletionCheck | null>(null)
  const [preferences, setPreferences] = useState<CompletionCheck | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const load = () => {
    setError(null)
    api.profile.completion().then(setProfile).catch(setError)
    api.preferences
      .completion()
      .then(setPreferences)
      .catch(() => setPreferences({ isComplete: false, missingFields: [] }))
  }

  useEffect(load, [])

  // **A failure has to be able to reach the screen.**
  //
  // This used to be `if (!profile) return <Busy />` with no case for error, so any failure of the
  // first call left the page showing "loading" for as long as anyone was willing to wait: setError
  // ran, and the early return meant the banner below it was never rendered. It was the first
  // screen of every new account, and it looked exactly like a slow server.
  if (error && !profile) {
    return (
      <div className="page stack">
        <h1>{t('onboarding.title')}</h1>
        <ErrorBanner error={error} />
        <button type="button" className="primary" onClick={load}>
          {t('common.retry')}
        </button>
      </div>
    )
  }

  if (!profile) {
    return <Busy />
  }

  return (
    <div className="page stack">
      <h1>{t('onboarding.title')}</h1>

      <ErrorBanner error={error} />

      {account && !account.emailConfirmed ? (
        <Notice>
          {t('verify.whyRequired')} <Link to={emailPaths.verifyEmail}>{t('verify.resend')}</Link>
        </Notice>
      ) : null}

      <section className="card">
        <h2>{t('completion.title')}</h2>

        {profile.isComplete ? (
          <Notice>{t('completion.ready')}</Notice>
        ) : (
          <>
            <p>{t('completion.missing')}</p>
            <ul className="missing-list">
              {profile.missingFields.map((field) => (
                <li key={field}>{translateField(field, t)}</li>
              ))}
            </ul>
          </>
        )}

        <div className="row-links">
          <Link to="/profile">{t('onboarding.step.profile')}</Link>
          <Link to="/photo">{t('onboarding.step.photo')}</Link>
          <Link to="/contact">{t('onboarding.step.contact')}</Link>
          <Link to="/preferences">{t('onboarding.step.preferences')}</Link>
        </div>
      </section>

      {profile.isComplete && preferences?.isComplete ? (
        <button
          type="button"
          className="primary"
          disabled={busy}
          onClick={() => {
            setError(null)
            setBusy(true)

            api.profile
              .get()
              .then((p) => api.profile.complete(p.version))
              .then(() => api.preferences.get())
              .then((p) => api.preferences.complete(p.version))
              .then(load)
              .catch(setError)
              .finally(() => setBusy(false))
          }}
        >
          {busy ? t('common.saving') : t('completion.markComplete')}
        </button>
      ) : null}
    </div>
  )
}

function translateField(field: string, t: (key: MessageKey) => string): string {
  const key = `completion.field.${field}` as MessageKey

  const translated = t(key)

  return translated === key ? field : translated
}
